import { HtmlHTMLAttributes, memo } from "react";
import classNames from "classnames";
import { CreditCardPreview, LoaderContainer, ProgressButton } from "../../../ui";
import { stripeDeclineErrors, stripeErrors } from "./stripeErrors";

import styles from "./style.module.scss";

type CreditCardCardProps = {
  cardBrand?: string | null
  cardLast4?: string | null
  cardHolderName?: string | null
  onChangeCardClick?: () => void
  disabled?: boolean,
  stripeErrorCode?: string | null,
  stripeDeclineCode?: string | null,
  isProcessing?: boolean,
  isFetching?: boolean,
} & HtmlHTMLAttributes<HTMLDivElement>

export const CreditCardCard = memo<CreditCardCardProps>((
  {
    cardLast4,
    cardBrand,
    cardHolderName,
    disabled,
    className,
    stripeDeclineCode,
    stripeErrorCode,
    onChangeCardClick,
    isProcessing = false,
    isFetching = false,
    ...rest
  }) => {
  const cardIsPresent = cardLast4 || cardBrand;

  let error = "";
  if (stripeDeclineCode) {
    error = stripeDeclineErrors[stripeDeclineCode]?.toString();
  } else if (stripeErrorCode) {
    error = stripeErrors[stripeErrorCode]?.toString();
  }

  let buttonText: string;
  let note: string = "";
  if (isProcessing) {
    buttonText = "Processing...";
    note = "Card will appear after processing.";
    disabled = true;
  } else {
    buttonText = cardIsPresent
      ? "Change the credit card"
      : "Add the credit card";
    if (!error) {
      note = "New card will be used for charging.";
    }
  }

  return (
    <div {...rest} className={classNames(styles.root, className, { [styles.error]: !!error })}>
      <LoaderContainer isLoading={isFetching}>
        <CreditCardPreview
          className={styles.cardPreview}
          cardHolderName={cardHolderName}
          cardLast4={cardLast4}
          cardBrand={cardBrand}
        />
        {!!error && <p title={error} className={styles.errorBlock}>{error}</p>}
        <ProgressButton
          className={styles.changeCard}
          buttonColorSchema="outlined"
          buttonType="secondary"
          type="button"
          onClick={onChangeCardClick}
          disabled={disabled}
        >
          {buttonText}
        </ProgressButton>
        {note && <p className={styles.info}>{note}</p>}
      </LoaderContainer>
    </div>
  );
});

export default CreditCardCard;
